import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../utils/auth";
import { sdkWrapperURL } from "../utils/api-url-list";

import LoadingScreen from "../components/LoadingScreen";

import StartScanBanner from "../assets/images/StartScanBanner.webp";
import { ReactComponent as GlowingHeart } from "../assets/icons/GlowingHeart.svg";
import { ReactComponent as Chevron } from "../assets/icons/Chevron.svg";

const Dashboard = () => {
  const {
    getToken,
    userData: { employee_id },
  } = useAuth();
  const [loading, setLoading] = useState(true);
  const [lastScanResult, setLastResult] = useState({});

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    (async () => {
      try {
        const token = await getToken();
        const scoreResp = await fetch(
          sdkWrapperURL("/users/sdk/test_api_private"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({
              action: "list_scan_UL",
              employee_id,
              table_config_rows_per_page: 1,
              table_config_page_no: 1,
            }),
            signal: controller.signal,
          }
        );
        const scoreRespJSON = await scoreResp.json();
        if (scoreRespJSON?.statusCode?.toString().startsWith("2")) {
          if (scoreRespJSON.result?.length > 0)
            setLastResult(scoreRespJSON.result[0]);
          else throw new Error("No recent scan data.");
        } else
          throw new Error(
            scoreRespJSON?.message ?? "Failed to fetch recent scan data."
          );
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return loading ? (
    <LoadingScreen style={{ minHeight: "60vh" }} />
  ) : (
    <section className="px-6 py-2">
      <div className="flex items-center space-x-4 px-6 py-4 rounded-2xl bg-gradient-transparent text-white">
        <GlowingHeart className="shrink-0 h-28 w-28" />
        <div>
          <p className="text-3xl font-bold">{lastScanResult.wellness_score}</p>
          <p className="text-secondary font-semibold leading-tight">
            Digital Health Assessment Score
          </p>
          <p className="mt-1 text-xxs font-light leading-snug whitespace-pre-line">
            {lastScanResult.created_at?.length > 0
              ? `Last updated:\n${new Date(
                  lastScanResult.created_at
                ).toLocaleDateString(undefined, {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}, ${new Date(lastScanResult.created_at).toLocaleTimeString(
                  undefined,
                  { hour: "2-digit", minute: "2-digit", hour12: true }
                )}`
              : "No scan data avaiable"}
          </p>
        </div>
      </div>

      {!(lastScanResult.created_at?.length > 0) ? (
        <Link
          to="/start-scan"
          className="my-6 flex items-center space-x-4 px-6 py-4 rounded-2xl bg-gradient-transparent text-white text-left"
        >
          <div>
            <h3 className="text-sm font-bold leading-tight tracking-tight">
              Start Your
              <br />
              Digital Health
              <br />
              Assessment
            </h3>
            <p className="mt-1.5 text-xxs leading-tight tracking-tight">
              Get personalized insights into your health and wellness today.
            </p>
            <p className="max-w-40 mt-3 px-4 py-2 rounded-full bg-white text-secondary text-xs font-medium text-center">
              Start Assessment
            </p>
          </div>
          <img className="shrink-0 h-24" src={StartScanBanner} alt="Banner" />
        </Link>
      ) : (
        <div className="my-6 flex items-center space-x-4 px-6 py-4 rounded-2xl bg-gradient-transparent text-white text-left">
          <div>
            <h3 className="text-sm font-bold leading-tight tracking-tight">
              Start Your
              <br />
              Digital Health
              <br />
              Assessment
            </h3>
            <p className="mt-1.5 text-xxs leading-tight tracking-tight">
              Get personalized insights into your health and wellness today.
            </p>
            <div className="mt-3 flex items-center space-x-1.5 text-success">
              <p className="text-xs font-medium tracking-tight">
                Assessment Completed
              </p>
              <svg className="shrink-0 h-4 w-4" viewBox="0 0 18 18" fill="none">
                <path
                  d="M9.0013 0.666016C4.4013 0.666016 0.667969 4.39935 0.667969 8.99935C0.667969 13.5993 4.4013 17.3327 9.0013 17.3327C13.6013 17.3327 17.3346 13.5993 17.3346 8.99935C17.3346 4.39935 13.6013 0.666016 9.0013 0.666016ZM7.33464 13.166L3.16797 8.99935L4.34297 7.82435L7.33464 10.8077L13.6596 4.48268L14.8346 5.66602L7.33464 13.166Z"
                  fill="currentColor"
                />
              </svg>
            </div>
          </div>
          <img className="shrink-0 h-24" src={StartScanBanner} alt="Banner" />
        </div>
      )}

      {lastScanResult.created_at?.length > 0 && (
        <Link
          to="/scan-result"
          state={{
            result: {
              scan_id: lastScanResult.scan_id,
              posture: lastScanResult.posture,
              wellness_score: lastScanResult.wellness_score,
              vitals: lastScanResult.vitals,
            },
          }}
          className="flex items-center justify-between px-6 py-4 rounded-2xl border border-secondary/60 bg-primary text-white text-sm font-medium"
        >
          <span>View Last Assessment Result</span>
          <Chevron className="shrink-0 h-4 w-4 -rotate-90" />
        </Link>
      )}

      <div className="my-8 space-y-4">
        <h2 className="text-sm font-semibold">
          About Digital Health Assessment
        </h2>
        <iframe
          className="rounded-xl w-full aspect-video outline-none bg-primary"
          title="Product Video"
          src="https://www.youtube.com/embed/LbJ70TaXErA?color=white&playsinline=1&fs=0&disablekb=1&rel=0&cc_load_policy=0&iv_load_policy=3"
        />
        <p className="text-xs text-justify font-light">
          Introducing the latest innovation in HbA1c testing: a convenient and
          non-invasive method utilizing face or finger scans. This cutting-edge
          technology offers a hassle-free and accessible approach to monitoring
          HbA1c levels, revolutionizing diabetes management for individuals
          worldwide. Currently in the beta phase, this new launch represents a
          significant step forward in improving diabetes care and empowering
          individuals to take control of their health.
        </p>
      </div>
    </section>
  );
};

export default Dashboard;
